import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { IActiveTabDetails, TabItem, Typography } from '@agilepoint/wat-react-ux';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { PlatformTypes } from 'modules/create-new-app/components/app-wizard/steps';
import { StyledActionButton, StyledPrimaryButton, StyledStackPanel } from 'common';
import { useCustomPopupEvents } from 'lib/hooks';
import { AndroidTabContent } from './android-tab-content';
import { IOSTabContent } from './ios-tab-content';
import { AndroidDescription, RelativePositionedFlexBox, ResetButton, StyledTabsPanel } from '../styled-components';
import { UploadImagesFormFields } from '../types';
import { DefaultUploadImages } from '../defaults';
import { createGlobalStyle } from 'styled-components';

interface IUploadImagesTabProps {
    platformTypes: string[];
    containerHeight?: string;
    headerLabel?: string;
    menuColor?: string;
}

const androidDetails = [{
    imagePickerFieldName: UploadImagesFormFields.androidAppLogoHDPIPortrait,
    resetImageURL: DefaultUploadImages.AppLogo
},
{
    imagePickerFieldName: UploadImagesFormFields.androidAppLogoXHDPIPortrait,
    resetImageURL: DefaultUploadImages.AppLogo
},
{
    imagePickerFieldName: UploadImagesFormFields.androidAppIconHDPIPortrait,
    resetImageURL: DefaultUploadImages.AppIcon
},
{
    imagePickerFieldName: UploadImagesFormFields.androidAppIconXXHDPIPortrait,
    resetImageURL: DefaultUploadImages.AppIcon
},
{
    imagePickerFieldName: UploadImagesFormFields.androidAppIconXHDPIPortrait,
    resetImageURL: DefaultUploadImages.AppIcon
},
{
    imagePickerFieldName: UploadImagesFormFields.androidSplashScreenHDPIPortrait,
    resetImageURL: DefaultUploadImages.SplashScreenPortraitHDPI
},
{
    imagePickerFieldName: UploadImagesFormFields.androidSplashScreenXHDPIPortrait,
    resetImageURL: DefaultUploadImages.SplashScreenPortraitXHDPI
},
{
    imagePickerFieldName: UploadImagesFormFields.androidSplashScreenXXHDPIPortrait,
    resetImageURL: DefaultUploadImages.SplashScreenPortraitXXHDPI
}];

const iosDetails = [
    //     {
    //     imagePickerFieldName: UploadImagesFormFields.iosAppIconiphone,
    //     resetImageURL: DefaultUploadImages.AppIcon
    // },
    // {
    //     imagePickerFieldName: UploadImagesFormFields.iosAppIconipad,
    //     resetImageURL: DefaultUploadImages.AppIcon
    // },
    {
        imagePickerFieldName: UploadImagesFormFields.iosAppDefaultIcon,
        resetImageURL: DefaultUploadImages.AppIcon
    },
    {
        imagePickerFieldName: UploadImagesFormFields.iosSplashScreeniphone,
        resetImageURL: DefaultUploadImages.SplashScreenPortrait
    },
    {
        imagePickerFieldName: UploadImagesFormFields.iosAppApplicationLogo,
        resetImageURL: DefaultUploadImages.AppIcon
    }
    // {
    //     imagePickerFieldName: UploadImagesFormFields.iosSplashScreenipad,
    //     resetImageURL: DefaultUploadImages.SplashScreenPortrait
    // }
];
// Note: temp code need to fix later
const BasicSettingsGlobalStyle1 = createGlobalStyle`
    .ap-tabsPanel-tabContentContainer {
        padding-top: 0px !important;
    }
`;

export const UploadImagesTab = (props: IUploadImagesTabProps) => {
    const { platformTypes, containerHeight, headerLabel, menuColor } = props;
    const { resetField } = useFormContext();
    const { showCustomPopup, removePopup } = useCustomPopupEvents();
    const [activeTab, setActiveTab] = useState('android' as React.Key);
    const translate = useTranslate();

    const removePopupHandler = useCallback(() => removePopup('reset-all-images'), [removePopup]);

    const onOKClick = useCallback(() => {
        if (activeTab === 'android') {
            androidDetails.forEach(({ imagePickerFieldName, resetImageURL }) => resetField(imagePickerFieldName, { keepDirty: false, defaultValue: resetImageURL }));
            removePopupHandler();
            return;
        }
        iosDetails.forEach(({ imagePickerFieldName, resetImageURL }) => resetField(imagePickerFieldName, { keepDirty: false, defaultValue: resetImageURL }));
        removePopupHandler();
    }, [activeTab, removePopupHandler, resetField]);

    const onTabChange = useCallback((tabDetails: IActiveTabDetails) => setActiveTab(tabDetails.activeTab), []);

    const onResetAllClick = useCallback(() => {
        showCustomPopup({
            headerTitle: translate('common.title'),
            id: 'reset-all-images',
            height: '185px',
            width: '536px',
            showModal: true,
            onRenderContent: () => (<Typography.Small>{activeTab === 'android'
                ? translate('modules.createNewApp.steps.uploadImages.messages.clearAndroidImages')
                : translate('modules.createNewApp.steps.uploadImages.messages.cleariOSimages')}</Typography.Small>),
            onRenderFooter: () => (
                <>
                    <StyledActionButton label={translate('common.actions.cancel')} onClick={removePopupHandler} />
                    <StyledPrimaryButton label={translate('common.actions.reset')} onClick={onOKClick} />
                </>
            )
        });
    }, [activeTab, onOKClick, removePopupHandler, showCustomPopup, translate]);

    const androidTab = useMemo(() => (
        <TabItem id="android" header={translate('modules.createNewApp.steps.uploadImages.androidHeader')}>
            <AndroidTabContent menuColor={menuColor} />
        </TabItem>
    ), [menuColor, translate]);

    const iosTab = useMemo(() => (
        <TabItem id="ios" header={translate('modules.createNewApp.steps.uploadImages.iOSHeader')}>
            <IOSTabContent />
        </TabItem>
    ), [translate]);

    const renderPlatformType = () => (
        platformTypes.includes(PlatformTypes.Android) ? androidTab : iosTab
    );

    return (
        <StyledStackPanel margin='30px 0px 0px 0px' gap="0" alignment="center" orientation='vertical' width="100%" height={containerHeight ?? 'calc(100% - 111px)'}>
            <Typography.H2 title={headerLabel ? headerLabel : translate('modules.createNewApp.steps.uploadImages.customImagesHeader')}>
                {headerLabel ? headerLabel : <Localize translate='modules.createNewApp.steps.uploadImages.customImagesHeader' />}
            </Typography.H2>
            <BasicSettingsGlobalStyle1 />
            <AndroidDescription title={translate('modules.createNewApp.steps.uploadImages.uploadImageValidationtText')}><Localize translate='modules.createNewApp.steps.uploadImages.uploadImageValidationtText' /></AndroidDescription>
            <RelativePositionedFlexBox flexDirection='column' width="100%" height={activeTab === 'android' ? 'calc(100% - 126px)' : 'calc(100% - 120px)'}>
                <ResetButton label={translate('modules.createNewApp.steps.uploadImages.resetAllLabel')} title={translate('common.actions.resetAll')} iconName='maa-reset.outline' onClick={onResetAllClick} />
                <StyledTabsPanel activeTab={activeTab} onChange={onTabChange} tabHeight={30}>
                    <>
                        {platformTypes.includes(PlatformTypes.Android) && platformTypes.includes(PlatformTypes.IOS) ?
                            <>
                                {androidTab}
                                {iosTab}
                            </>
                            : renderPlatformType()}
                    </>

                </StyledTabsPanel>
            </RelativePositionedFlexBox>
        </StyledStackPanel>
    );
};

